import { useEffect, useState } from "react";
import {
  Button,
  ButtonGroup,
  Dialog,
  DialogContainer,
  Content,
  Heading,
  TextArea,
  ActionButton,
  Text,
} from "@react-spectrum/s2";
import StarIcon from "@react-spectrum/s2/icons/Star";
import StarFilledIcon from "@react-spectrum/s2/icons/StarFilled";
import { style } from "@react-spectrum/s2/style" with { type: "macro" };

import { useSubmitFeedback } from "@app/hooks/feedback";

const MAX_RATING = 5;

const StarRating = ({ value, onChange }) => {
  const [hovered, setHovered] = useState(0);

  return (
    <div
      role="group"
      aria-label="Rating"
      className={style({ display: "flex" })}
    >
      {Array.from({ length: MAX_RATING }, (_, index) => index + 1).map(
        (star) => {
          const filled = star <= (hovered || value);
          return (
            <ActionButton
              key={star}
              isQuiet
              aria-label={`${star} star${star > 1 ? "s" : ""}`}
              onPress={() => onChange(star)}
              onHoverStart={() => setHovered(star)}
              onHoverEnd={() => setHovered(0)}
            >
              {filled ? <StarFilledIcon /> : <StarIcon />}
            </ActionButton>
          );
        },
      )}
    </div>
  );
};

// Shared rating + comment fields used by both the dialog and inline layouts.
const FeedbackFields = ({
  rating,
  setRating,
  comment,
  setComment,
  isDisabled,
}) => (
  <div
    className={style({
      display: "flex",
      flexDirection: "column",
      rowGap: 16,
    })}
  >
    <div>
      <div className={style({ font: "ui", marginBottom: 8 })}>Rating</div>
      <StarRating value={rating} onChange={setRating} />
    </div>
    <TextArea
      label="Comment"
      value={comment}
      onChange={setComment}
      isRequired={rating < 4}
      isDisabled={isDisabled}
    />
  </div>
);

/**
 * Lets the user rate and comment on a video. Renders either as a controlled
 * Spectrum Dialog (default) triggered by the parent, or inline (`variant="inline"`)
 * as an always-visible widget below a video preview.
 *
 * @param {object} props
 * @param {{ id: string }} props.video - The video the feedback is about.
 * @param {"dialog" | "inline"} [props.variant] - Display mode (default "dialog").
 * @param {boolean} [props.isOpen] - Dialog mode: whether the dialog is open.
 * @param {() => void} [props.onClose] - Dialog mode: called when it should close.
 * @param {string} [props.currentStep] - Overrides the step recorded with the
 *   feedback (e.g. "video-view"); defaults to the video's persisted step.
 * @param {"auto" | "manual" | "inline"} [props.method] - How the feedback was triggered.
 * @param {string} [props.styles] - Inline mode: extra styles for the container.
 */
export const UserFeedback = ({
  video,
  variant = "dialog",
  title = "Share your feedback",
  isOpen,
  onClose,
  currentStep,
  method,
  styles,
}) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const submitFeedback = useSubmitFeedback();

  const isInline = variant === "inline";

  const isSubmitDisabled =
    rating < 1 || (rating < 4 && !comment) || submitFeedback.isPending;

  // Dialog mode: reset the form whenever it closes so it reopens fresh.
  useEffect(() => {
    if (!isInline && !isOpen) {
      setRating(0);
      setComment("");
    }
  }, [isInline, isOpen]);

  const handleSubmit = () => {
    submitFeedback.mutate(
      { videoId: video?.id, rating, comment, currentStep, method },
      {
        onSuccess: () => {
          if (isInline) {
            setSubmitted(true);
          } else {
            onClose?.();
          }
        },
      },
    );
  };

  if (isInline) {
    return (
      <div
        className={
          style({
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            rowGap: 16,
            padding: { default: 24, md: 32 },
            borderRadius: "xl",
            backgroundColor: "layer-2",
          }) + styles
        }
      >
        {submitted ? (
          <div
            className={style({
              display: "flex",
              alignItems: "center",
              gap: 8,
              font: "body",
            })}
          >
            <StarFilledIcon />
            <Text>Thanks for your feedback!</Text>
          </div>
        ) : (
          <>
            <div className={style({ font: "heading-sm" })}>{title}</div>
            <FeedbackFields
              rating={rating}
              setRating={setRating}
              comment={comment}
              setComment={setComment}
              isDisabled={submitFeedback.isPending}
            />
            <Button
              variant="primary"
              onPress={handleSubmit}
              isDisabled={isSubmitDisabled}
              styles={style({ marginStart: "auto", marginTop: 16 })}
            >
              <Text>Submit</Text>
            </Button>
          </>
        )}
      </div>
    );
  }

  return (
    <DialogContainer onDismiss={() => onClose?.()}>
      {isOpen && (
        <Dialog size="S">
          <Heading slot="title">{title}</Heading>
          <Content>
            <FeedbackFields
              rating={rating}
              setRating={setRating}
              comment={comment}
              setComment={setComment}
              isDisabled={submitFeedback.isPending}
            />
          </Content>
          <ButtonGroup>
            <Button
              variant="secondary"
              onPress={() => onClose?.()}
              isDisabled={submitFeedback.isPending}
            >
              <Text>Cancel</Text>
            </Button>
            <Button
              variant="accent"
              onPress={handleSubmit}
              isDisabled={isSubmitDisabled}
            >
              <Text>Submit</Text>
            </Button>
          </ButtonGroup>
        </Dialog>
      )}
    </DialogContainer>
  );
};
